import React, { useState, useEffect, useRef } from "react";
import { OpenAI } from "openai";
import caseStudySchema from "../../schemas/caseStudySchema";
import { Grid, Paper, Typography, Box, Divider, CircularProgress, Button } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import Flashcard from "../Flashcard";
import FlowDiagram from "../FlowDiagram";

const CaseStudyProps = {
  courseName: String,
  module: String,
  onComplete: Function,
};

const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY || "API_KEY_NOT_FOUND",
  dangerouslyAllowBrowser: true,
});

const protocolSteps = [
  {
    id: 0,
    title: "Initial Response",
    description:
      "Do not move the patient, call for immediate assistance, and check the patient's Airway, Breathing, and Circulation (ABCs) to ensure their safety and stability before proceeding with any further assessment or treatment.",
  },
  {
    id: 1,
    title: "Primary Assessment",
    description:
      "Conduct a thorough check of the patient's consciousness and vital signs, including their level of consciousness, breathing rate and quality, and circulation status, to quickly identify any potential life-threatening conditions.",
  },
  {
    id: 2,
    title: "Comprehensive Evaluation",
    description:
      "Perform a detailed and systematic assessment of the patient's injuries and underlying medical conditions, including a thorough examination for visible injuries, pain points, and neurological status, to inform the development of an effective treatment plan.",
  },
  {
    id: 3,
    title: "Communication",
    description:
      "Notify all relevant parties, including the patient's physician, family members, and other healthcare providers, as needed, to ensure that everyone is informed and aligned regarding the patient's condition and treatment plan.",
  },
  {
    id: 4,
    title: "Monitoring",
    description:
      "Implement a comprehensive monitoring protocol, including the setup of vital signs monitoring and a regular observation schedule, to closely track the patient's condition and quickly identify any changes or complications that may require intervention.",
  },
];

const flashcards = [
  {
    question:
      "What are the key immediate actions to take after finding Mrs. Vance on the floor?",
    options: [
      "Move her to bed, check vital signs, call family",
      "Do not move her, call for assistance, check ABCs",
      "Help her stand up, give water, document incident",
      "Call emergency services, start CPR, notify supervisor",
    ],
    correctAnswer: 1,
    explanation:
      "It's crucial not to move a fallen patient until properly assessed. Call for assistance and check ABCs (Airway, Breathing, Circulation) first.",
    relatedStep: 0,
  },
  {
    question: "Which assessment should be prioritized first for Mrs. Vance?",
    options: [
      "Detailed medication review",
      "Musculoskeletal examination",
      "Consciousness and vital signs",
      "Environmental hazard assessment",
    ],
    correctAnswer: 2,
    explanation:
      "Initial assessment must focus on consciousness and vital signs to ensure patient stability before proceeding with other examinations.",
    relatedStep: 1,
  },
  {
    question:
      "What specific aspects need focus during Mrs. Vance's comprehensive evaluation?",
    options: [
      "Only cognitive function due to mild impairment",
      "Just blood pressure due to hypertension",
      "Cardiac, neurological, and musculoskeletal assessment",
      "Only check for visible injuries",
    ],
    correctAnswer: 2,
    explanation:
      "Given her conditions (hypertension, cognitive impairment, osteoarthritis), a thorough assessment of all these systems is crucial.",
    relatedStep: 2,
  },
  {
    question: "Who needs to be notified about Mrs. Vance's fall?",
    options: [
      "Only the supervisor",
      "Family and supervisor only",
      "Physician, family, and OT/PT specialists",
      "Just the emergency services",
    ],
    correctAnswer: 2,
    explanation:
      "All relevant parties need to be notified to ensure proper care coordination, especially given her multiple conditions.",
    relatedStep: 3,
  },
  {
    question: "How should Mrs. Vance's vital signs be monitored?",
    options: [
      "Once daily for a week",
      "Every 4 hours for 24 hours",
      "Hourly for 4 hours, then every 4 hours for 24 hours",
      "Only when symptoms appear",
    ],
    correctAnswer: 2,
    explanation:
      "Close monitoring is crucial in the first 4 hours, followed by regular checks for the next 24 hours, especially due to her hypertension.",
    relatedStep: 4,
  },
];

// Debug log to check if environment variable is loaded
console.log("API Key available:", !!process.env.REACT_APP_OPENAI_API_KEY);

const CaseStudyGenerator = ({ courseName, module, onComplete }) => {
  const [caseStudy, setCaseStudy] = useState(null);
  const [error, setError] = useState(null);
  const [activeCaseStudy, setActiveCaseStudy] = useState("case1");
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState(0);
  const [courseCompleted, setCourseCompleted] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [expandedStep, setExpandedStep] = useState(null);
  const [correctlyAnsweredQuestions, setCorrectlyAnsweredQuestions] = useState(
    []
  );
  const [shouldResetIndex, setShouldResetIndex] = useState(false);
  const [activeSection, setActiveSection] = useState("case1");
  const [incorrectAttempts, setIncorrectAttempts] = useState(0);
  const [flashcards, setFlashcards] = useState([]);
  const [additionalCaseIncorrectAttempts, setAdditionalCaseIncorrectAttempts] =
    useState(0);
  const [currentCaseStudyNumber, setCurrentCaseStudyNumber] = useState(1);
  const hasGeneratedCaseStudy = useRef(false);
  const MAX_ATTEMPTS = 2;

  const loadingSteps = [
    "Generating a case study for you...",
    "Creating questions based on the case study...",
    "Preparing the learning materials...",
  ];

  const ErrorCrosses = () => {
    const attempts =
      activeCaseStudy === "case2"
        ? additionalCaseIncorrectAttempts
        : incorrectAttempts;

    return (
      <Box
        sx={{
          position: "absolute",
          top: -24,
          right: 24,
          display: "flex",
          gap: 1.5,
          padding: "4px 12px",
          borderRadius: "12px",
          backgroundColor: "background.paper",
          boxShadow: 2,
          border: 1,
          borderColor: "divider",
        }}
      >
        {[...Array(3)].map((_, index) => (
          <CloseIcon
            key={index}
            sx={{
              color: index < attempts ? "error.main" : "text.disabled",
              fontSize: "1.75rem",
              filter:
                index < attempts
                  ? "drop-shadow(0 0 2px rgba(211, 47, 47, 0.5))"
                  : "none",
              transition: "all 0.3s ease",
            }}
          />
        ))}
      </Box>
    );
  };

  const handleStepClick = (stepId) => {
    setExpandedStep(expandedStep === stepId ? null : stepId);
  };

  const handleCorrectAnswer = (cardIndex) => {
    setCorrectlyAnsweredQuestions((prev) => [...prev, cardIndex]);
    const relatedStep = flashcards[cardIndex]?.relatedStep;
    if (relatedStep !== undefined && !completedSteps.includes(relatedStep)) {
      setCompletedSteps((prev) => [...prev, relatedStep]);
    }

    // Check if all questions are answered correctly
    if (correctlyAnsweredQuestions.length + 1 === flashcards.length) {
      setCourseCompleted(true);
      if (currentCaseStudyNumber === 2) {
        // If this is the second case study, notify parent component
        onComplete?.();
      }
    }
  };

  const handleIncorrectAnswer = () => {
    setIncorrectAttempts((prev) => prev + 1);

    // If max attempts reached and haven't generated a new case study yet, generate one
    if (incorrectAttempts >= MAX_ATTEMPTS && currentCaseStudyNumber === 1) {
      setError("Generating new case study...");
      setShouldResetIndex(true);
      setIncorrectAttempts(0);
      setCurrentCaseStudyNumber(2);
      generateCaseStudy().catch((err) => {
        console.error("Error generating new case study:", err);
        setError("Failed to generate new case study. Please try again.");
      });
    } else if (
      incorrectAttempts >= MAX_ATTEMPTS &&
      currentCaseStudyNumber === 2
    ) {
      setError(
        "You have reached the maximum number of attempts for both case studies."
      );
    }
  };

  const generateCaseStudy = async () => {
    console.log("case study generator called. ");
    try {
      setIsLoading(true);
      setLoadingStep(0);

      const prompt = `Generate a structured case study with 5 flashcards for the course "${courseName}" and module "${module}". Each flashcard must be mapped to one of the 5 protocol steps. Format it as JSON with the following structure: 
      {
        "caseStudy": {
          "title": "",
          "patientInfo": {
            "name": "",
            "age": 0,
            "position": "{position is the profession of the patient}",
            "conditions": []
          },
          "objectives": [],
          "scenario": {
            "description": ""
          },
          "keyPoints": []
        },
        "flashcards": [
          {
            "question": "",
            "options": ["", "", "", ""],
            "correctAnswer": 0,
            "explanation": "",
            "relatedStep": "Initial Response"
          },
          {
            "question": "",
            "options": ["", "", "", ""],
            "correctAnswer": 0,
            "explanation": "",
            "relatedStep": "Primary Assessment"
          },
          {
            "question": "",
            "options": ["", "", "", ""],
            "correctAnswer": 0,
            "explanation": "",
            "relatedStep": "Comprehensive Evaluation"
          },
          {
            "question": "",
            "options": ["", "", "", ""],
            "correctAnswer": 0,
            "explanation": "",
            "relatedStep": "Communication"
          },
          {
            "question": "",
            "options": ["", "", "", ""],
            "correctAnswer": 0,
            "explanation": "",
            "relatedStep": "Monitoring"
          }
        ]
      }`;

      const response = await openai.chat.completions.create({
        model: "gpt-4",
        messages: [{ role: "user", content: prompt }],
        temperature: 0.7,
      });

      const content = response.choices[0]?.message?.content;
      if (!content) throw new Error("Failed to generate content");

      const parsedData = JSON.parse(content);
      const validatedCaseStudy = caseStudySchema.parse(parsedData.caseStudy);
      setCaseStudy(validatedCaseStudy);
      setFlashcards(parsedData.flashcards);
      setError(null);
      setLoadingStep(1);

      // Small delay for smooth transition
      await new Promise((resolve) => setTimeout(resolve, 500));
      setLoadingStep(2);
      setIsLoading(false);

      return validatedCaseStudy;
    } catch (err) {
      if (err instanceof Error) {
        setError(`Error: ${err.message}`);
      } else {
        setError("An unknown error occurred");
      }
      setCaseStudy(null);
      setFlashcards([]);
      setIsLoading(false);
      throw err;
    }
  };

  // Only generate case study if module is available
  useEffect(() => {
    if (module) {
      generateCaseStudy().catch((err) => {
        console.error("Error initializing data:", err);
        setError("Failed to load the case study. Please try again.");
        setIsLoading(false);
      });
    }
  }, [module]);

  if (!module) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={3}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="primary">
          Loading module...
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={3}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="primary">
          {loadingSteps[loadingStep]}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This may take a few moments...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        gap={2}
      >
        <Typography color="error" variant="h6">
          {error}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  if (!caseStudy) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        gap={2}
      >
        <Typography variant="h6">No case study available</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
        >
          Reload
        </Button>
      </Box>
    );
  }

  return (
    <div>
      <Grid container spacing={3}>
        {/* Case Study Section */}
        <Grid item xs={12} md={3}>
          <Paper elevation={3} sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom color="primary">
              Case Study {currentCaseStudyNumber}
            </Typography>
            <Typography variant="h6" gutterBottom color="primary">
              {caseStudy?.title}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                gutterBottom
              >
                Patient Information
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Name:</strong> {caseStudy.patientInfo.name}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Age:</strong> {caseStudy.patientInfo.age}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Position:</strong> {caseStudy.patientInfo.position}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                gutterBottom
              >
                Underlying Conditions
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                {caseStudy.patientInfo.conditions.map((condition, index) => (
                  <Typography
                    component="li"
                    variant="body2"
                    key={index}
                    gutterBottom
                  >
                    {condition}
                  </Typography>
                ))}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                gutterBottom
              >
                Scenario
              </Typography>
              <Typography variant="body2" sx={{ pl: 2, mt: 1 }}>
                {caseStudy.scenario.description}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                gutterBottom
              >
                Key Points
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                {caseStudy.keyPoints.map((point, index) => (
                  <Typography
                    component="li"
                    variant="body2"
                    key={index}
                    gutterBottom
                  >
                    {point}
                  </Typography>
                ))}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                gutterBottom
              >
                Learning Objectives
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                {caseStudy.objectives.map((objective, index) => (
                  <Typography
                    component="li"
                    variant="body2"
                    key={index}
                    gutterBottom
                  >
                    {objective}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Flashcard Section */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              height: "100%",
              minHeight: "500px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              align="center"
              color="primary"
            >
              Review Key Concepts
            </Typography>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              {courseCompleted ? (
                <Box sx={{ textAlign: "center", mt: 4 }}>
                  <Typography variant="h5" gutterBottom color="success.main">
                    <CheckCircleIcon sx={{ fontSize: 40, mb: -1, mr: 1 }} />
                    Course Completed!
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    You have successfully completed the case study.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setActiveSection("video2")}
                    sx={{ mt: 2 }}
                  >
                    Proceed to Video Assessment
                  </Button>
                </Box>
              ) : (
                <Flashcard
                  cards={flashcards}
                  onCorrectAnswer={handleCorrectAnswer}
                  onIncorrectAnswer={handleIncorrectAnswer}
                  correctlyAnswered={correctlyAnsweredQuestions}
                  resetIndex={shouldResetIndex}
                />
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Flow Diagram Section */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={3}
            sx={{ p: 2, height: "100%", position: "relative" }}
          >
            <ErrorCrosses />
            <Typography variant="h6" gutterBottom color="primary">
              Protocol Steps
            </Typography>
            <FlowDiagram
              steps={protocolSteps}
              completedSteps={completedSteps}
              expandedStep={expandedStep}
              onStepClick={handleStepClick}
              showDetails={true}
            />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default CaseStudyGenerator;
